import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, Legend,
} from "recharts";
import {
  TrendUp, Receipt, Coins, ChartBar, Warning, Money, CreditCard,
  Storefront, Ticket, Percent, ArrowRight,
} from "@phosphor-icons/react";
import { fmt, setCurrency } from "@/lib/format";

const RANGES = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7d" },
  { key: "30d", label: "30d" },
  { key: "mtd", label: "MTD" },
  { key: "custom", label: "Custom" },
];

const PIE_COLORS = ["#D62A35", "#F4A261", "#E9C46A", "#3B8154", "#7A6B63", "#A91B27", "#264653", "#E76F51"];

function Stat({ label, value, icon: Icon, accent, hint }) {
  return (
    <div className="bg-white border border-boba-stoneLine rounded-md p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-boba-inkSoft font-mono">{label}</div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center ${accent}`}>
          <Icon size={18} weight="duotone" />
        </div>
      </div>
      <div className="font-mono text-2xl font-bold mt-3 text-boba-ink">{value}</div>
      {hint && <div className="text-[11px] text-boba-inkSoft mt-1">{hint}</div>}
    </div>
  );
}

function SectionCard({ title, action, children, className = "" }) {
  return (
    <div className={`bg-white border border-boba-stoneLine rounded-md p-5 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-bold">{title}</h3>
        {action}
      </div>
      {children}
    </div>
  );
}

export default function Dashboard() {
  const [period, setPeriod] = useState("7d");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [staffId, setStaffId] = useState("");

  const [categories, setCategories] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/settings").then((r) => setCurrency(r.data.currency));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/staff").then((r) => setStaffList(r.data));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ period });
      if (period === "custom" && dateFrom && dateTo) {
        params.set("date_from", new Date(dateFrom).toISOString());
        const end = new Date(dateTo);
        end.setDate(end.getDate() + 1);
        params.set("date_to", end.toISOString());
      }
      if (categoryId) params.set("category_id", categoryId);
      if (staffId) params.set("staff_id", staffId);
      const { data } = await api.get(`/dashboard/analytics?${params.toString()}`);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (period !== "custom" || (dateFrom && dateTo)) load();
  }, [period, dateFrom, dateTo, categoryId, staffId]); // eslint-disable-line react-hooks/exhaustive-deps

  const paymentPie = useMemo(() => {
    if (!data) return [];
    return data.by_payment.map((p, i) => ({
      name: p.method.toUpperCase(),
      value: p.revenue,
      fill: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [data]);

  const categoryPie = useMemo(() => {
    if (!data) return [];
    return data.by_category.slice(0, 6).map((c, i) => ({
      name: c.name,
      value: c.revenue,
      fill: PIE_COLORS[i % PIE_COLORS.length],
    }));
  }, [data]);

  const maxHour = useMemo(() => {
    if (!data) return null;
    const peak = [...data.by_hour].sort((a, b) => b.revenue - a.revenue)[0];
    return peak && peak.revenue > 0 ? peak : null;
  }, [data]);

  const maxWeekday = useMemo(() => {
    if (!data) return null;
    const peak = [...data.by_weekday].sort((a, b) => b.revenue - a.revenue)[0];
    return peak && peak.revenue > 0 ? peak : null;
  }, [data]);

  return (
    <div className="h-screen flex flex-col">
      <div className="px-6 pt-6 pb-4 bg-white border-b border-boba-stoneLine">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-bold">Dashboard</h1>
            <p className="text-sm text-boba-inkSoft mt-1">
              {data?.range?.label || "—"}
              <span className="ml-2 text-xs font-mono text-boba-inkSoft">
                {data?.range?.from && new Date(data.range.from).toLocaleDateString()} →{" "}
                {data?.range?.to && new Date(new Date(data.range.to).getTime() - 1).toLocaleDateString()}
              </span>
            </p>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-2 flex-wrap" data-testid="dashboard-filters">
            <div className="inline-flex bg-boba-cream border border-boba-stoneLine rounded-md p-1" data-testid="range-picker">
              {RANGES.map((r) => (
                <button
                  key={r.key}
                  onClick={() => setPeriod(r.key)}
                  className={`px-3 py-1.5 rounded text-xs font-mono font-semibold transition-colors ${
                    period === r.key
                      ? "bg-boba-matcha text-white"
                      : "text-boba-inkSoft hover:bg-white"
                  }`}
                  data-testid={`range-${r.key}`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {period === "custom" && (
              <div className="flex items-center gap-1">
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="h-9 px-2 border border-boba-stoneLine rounded text-xs font-mono"
                  data-testid="date-from"
                />
                <ArrowRight size={12} className="text-boba-inkSoft" />
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="h-9 px-2 border border-boba-stoneLine rounded text-xs font-mono"
                  data-testid="date-to"
                />
              </div>
            )}

            {/* Outlet (placeholder for multi-outlet) */}
            <div className="inline-flex items-center gap-2 h-9 px-3 border border-boba-stoneLine rounded-md bg-white text-xs">
              <Storefront size={14} weight="duotone" className="text-boba-matcha" />
              <select
                value=""
                onChange={() => {}}
                disabled
                className="bg-transparent outline-none font-medium"
                data-testid="outlet-filter"
              >
                <option value="">Hizli (all outlets)</option>
              </select>
            </div>

            <select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="h-9 px-3 border border-boba-stoneLine rounded-md bg-white text-xs font-medium"
              data-testid="category-filter"
            >
              <option value="">All categories</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>

            <select
              value={staffId}
              onChange={(e) => setStaffId(e.target.value)}
              className="h-9 px-3 border border-boba-stoneLine rounded-md bg-white text-xs font-medium"
              data-testid="staff-filter"
            >
              <option value="">All staff</option>
              {staffList.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto scroll-thin p-6 space-y-6">
        {loading && !data && <div className="text-center text-stone-500 py-12">Loading analytics…</div>}

        {data && (
          <>
            {/* KPI Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="kpi-grid">
              <Stat label="Revenue" value={fmt(data.kpis.revenue)} icon={Coins} accent="bg-boba-matcha/10 text-boba-matcha" />
              <Stat label="Orders" value={data.kpis.orders} icon={Receipt} accent="bg-boba-peach/10 text-boba-peach" />
              <Stat label="Avg Ticket" value={fmt(data.kpis.avg_ticket)} icon={TrendUp} accent="bg-boba-honey/20 text-boba-ink" />
              <Stat label="Items Sold" value={data.kpis.items_sold} icon={ChartBar} accent="bg-stone-100 text-stone-700" />
              <Stat label="Cash" value={fmt(data.kpis.cash_revenue)} icon={Money} accent="bg-green-50 text-green-700"
                hint={`${data.kpis.revenue > 0 ? Math.round((data.kpis.cash_revenue / data.kpis.revenue) * 100) : 0}% of revenue`} />
              <Stat label="Card" value={fmt(data.kpis.card_revenue)} icon={CreditCard} accent="bg-blue-50 text-blue-700"
                hint={`${data.kpis.revenue > 0 ? Math.round((data.kpis.card_revenue / data.kpis.revenue) * 100) : 0}% of revenue`} />
              <Stat label="Discounts" value={fmt(data.kpis.discounts)} icon={Percent} accent="bg-amber-50 text-amber-700" />
              <Stat label="Voided" value={data.kpis.voided_count} icon={Warning} accent="bg-red-50 text-boba-danger"
                hint={`Open: ${data.kpis.open_count}`} />
            </div>

            {/* Trend charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionCard title="Revenue Trend" className="lg:col-span-2">
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={data.by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                    <XAxis dataKey="label" stroke="#7A6B63" fontSize={11} />
                    <YAxis stroke="#7A6B63" fontSize={11} />
                    <Tooltip
                      contentStyle={{ border: "1px solid #E5E0D8", borderRadius: 6, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                      formatter={(v) => fmt(v)}
                    />
                    <Bar dataKey="revenue" fill="#D62A35" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </SectionCard>

              <SectionCard title="Orders Trend">
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={data.by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                    <XAxis dataKey="label" stroke="#7A6B63" fontSize={11} />
                    <YAxis stroke="#7A6B63" fontSize={11} />
                    <Tooltip contentStyle={{ border: "1px solid #E5E0D8", borderRadius: 6, fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                    <Line type="monotone" dataKey="orders" stroke="#F4A261" strokeWidth={3} dot={{ fill: "#F4A261", r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              </SectionCard>
            </div>

            {/* Hour / Weekday */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard
                title="Peak Hours"
                action={maxHour && (
                  <span className="text-xs font-mono text-boba-matcha">
                    Busiest: {maxHour.label} · {fmt(maxHour.revenue)}
                  </span>
                )}
              >
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.by_hour}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                    <XAxis dataKey="hour" stroke="#7A6B63" fontSize={10} />
                    <YAxis stroke="#7A6B63" fontSize={11} />
                    <Tooltip
                      contentStyle={{ border: "1px solid #E5E0D8", borderRadius: 6, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                      labelFormatter={(h) => `${String(h).padStart(2, "0")}:00`}
                      formatter={(v, k) => k === "revenue" ? fmt(v) : v}
                    />
                    <Bar dataKey="revenue" fill="#E9C46A" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </SectionCard>

              <SectionCard
                title="By Day of Week"
                action={maxWeekday && (
                  <span className="text-xs font-mono text-boba-matcha">
                    Best: {maxWeekday.label} · {fmt(maxWeekday.revenue)}
                  </span>
                )}
              >
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={data.by_weekday}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E0D8" />
                    <XAxis dataKey="label" stroke="#7A6B63" fontSize={11} />
                    <YAxis stroke="#7A6B63" fontSize={11} />
                    <Tooltip
                      contentStyle={{ border: "1px solid #E5E0D8", borderRadius: 6, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                      formatter={(v) => fmt(v)}
                    />
                    <Bar dataKey="revenue" fill="#2B5C38" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </SectionCard>
            </div>

            {/* Tables */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Top Products · By Revenue">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="top-revenue-table">
                    <thead className="text-[10px] uppercase tracking-widest text-boba-inkSoft font-mono">
                      <tr>
                        <th className="text-left pb-2">Item</th>
                        <th className="text-right pb-2">Qty</th>
                        <th className="text-right pb-2">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_items_by_revenue.map((it, i) => (
                        <tr key={i} className="border-t border-boba-stoneLine">
                          <td className="py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-[10px] w-5 h-5 rounded-full bg-boba-matcha/10 text-boba-matcha flex items-center justify-center">{i + 1}</span>
                              <span className="font-semibold">{it.name}</span>
                            </div>
                          </td>
                          <td className="text-right font-mono text-boba-inkSoft">{it.qty}</td>
                          <td className="text-right font-mono font-semibold text-boba-matcha">{fmt(it.revenue)}</td>
                        </tr>
                      ))}
                      {data.top_items_by_revenue.length === 0 && (
                        <tr><td colSpan={3} className="text-center text-boba-inkSoft py-6">No sales</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </SectionCard>

              <SectionCard title="Top Products · By Quantity">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="top-qty-table">
                    <thead className="text-[10px] uppercase tracking-widest text-boba-inkSoft font-mono">
                      <tr>
                        <th className="text-left pb-2">Item</th>
                        <th className="text-right pb-2">Qty</th>
                        <th className="text-right pb-2">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.top_items_by_qty.map((it, i) => (
                        <tr key={i} className="border-t border-boba-stoneLine">
                          <td className="py-2.5">
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-[10px] w-5 h-5 rounded-full bg-boba-peach/20 text-boba-peachDark flex items-center justify-center">{i + 1}</span>
                              <span className="font-semibold">{it.name}</span>
                            </div>
                          </td>
                          <td className="text-right font-mono font-bold">{it.qty}</td>
                          <td className="text-right font-mono text-boba-inkSoft">{fmt(it.revenue)}</td>
                        </tr>
                      ))}
                      {data.top_items_by_qty.length === 0 && (
                        <tr><td colSpan={3} className="text-center text-boba-inkSoft py-6">No sales</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </div>

            {/* Category & Payment */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <SectionCard title="Category Mix">
                {categoryPie.length === 0 ? (
                  <div className="text-sm text-boba-inkSoft py-8 text-center">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={categoryPie} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80} paddingAngle={2}>
                        {categoryPie.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <div className="mt-3 space-y-1 text-xs font-mono">
                  {data.by_category.map((c, i) => (
                    <div key={i} className="flex justify-between text-boba-inkSoft">
                      <span>{c.name}</span>
                      <span>{c.qty} · {fmt(c.revenue)}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Order Sources" data-testid="sources-card">
                {(!data.by_source || data.by_source.length === 0) ? (
                  <div className="text-sm text-boba-inkSoft py-8 text-center">No sales yet</div>
                ) : (
                  <div className="space-y-3" data-testid="dashboard-by-source">
                    {data.by_source.map((s) => {
                      const styles = s.source === "swiggy"
                        ? { bar: "bg-[#FC8019]", dot: "bg-[#FC8019]", label: "Swiggy" }
                        : s.source === "zomato"
                          ? { bar: "bg-[#E23744]", dot: "bg-[#E23744]", label: "Zomato" }
                          : { bar: "bg-boba-matcha", dot: "bg-boba-matcha", label: "In-store" };
                      const total = data.kpis.revenue || 1;
                      const pct = Math.round((s.revenue / total) * 100);
                      return (
                        <div key={s.source}>
                          <div className="flex items-center justify-between text-xs font-mono">
                            <div className="flex items-center gap-2">
                              <span className={`w-2 h-2 rounded-full ${styles.dot}`} />
                              <span className="font-semibold">{styles.label}</span>
                              <span className="text-boba-inkSoft">{s.orders} orders</span>
                            </div>
                            <div className="text-right">
                              <div className="font-semibold text-boba-ink">{fmt(s.revenue)}</div>
                              {s.commission > 0 && (
                                <div className="text-[10px] text-boba-inkSoft">− {fmt(s.commission)} comm.</div>
                              )}
                            </div>
                          </div>
                          <div className="mt-1.5 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                            <div className={`h-full ${styles.bar}`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                    {data.commission_total > 0 && (
                      <div className="mt-3 pt-3 border-t border-boba-stoneLine text-xs font-mono flex justify-between text-boba-inkSoft">
                        <span>Total commission paid</span>
                        <span className="text-[#B85A0F] font-semibold">{fmt(data.commission_total)}</span>
                      </div>
                    )}
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Payment Split">
                {paymentPie.length === 0 ? (
                  <div className="text-sm text-boba-inkSoft py-8 text-center">No data</div>
                ) : (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={paymentPie} dataKey="value" nameKey="name" innerRadius={40} outerRadius={80}>
                        {paymentPie.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} contentStyle={{ fontFamily: "IBM Plex Mono", fontSize: 12 }} />
                      <Legend wrapperStyle={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} />
                    </PieChart>
                  </ResponsiveContainer>
                )}
                <div className="mt-3 space-y-1 text-xs font-mono">
                  {data.by_payment.map((p, i) => (
                    <div key={i} className="flex justify-between text-boba-inkSoft">
                      <span>{p.method.toUpperCase()}</span>
                      <span>{p.count} orders · {fmt(p.revenue)}</span>
                    </div>
                  ))}
                </div>
              </SectionCard>

              <SectionCard title="Staff Performance">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="staff-table">
                    <thead className="text-[10px] uppercase tracking-widest text-boba-inkSoft font-mono">
                      <tr>
                        <th className="text-left pb-2">Staff</th>
                        <th className="text-right pb-2">Orders</th>
                        <th className="text-right pb-2">Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.by_staff.map((s, i) => (
                        <tr key={i} className="border-t border-boba-stoneLine">
                          <td className="py-2.5 font-semibold">{s.name}</td>
                          <td className="text-right font-mono">{s.orders}</td>
                          <td className="text-right font-mono font-semibold text-boba-matcha">{fmt(s.revenue)}</td>
                        </tr>
                      ))}
                      {data.by_staff.length === 0 && (
                        <tr><td colSpan={3} className="text-center text-boba-inkSoft py-6">No sales</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            </div>

            {/* Low stock + Slow movers */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard
                title="Low Stock Alerts"
                action={data.low_stock.length > 0 && <Warning size={18} weight="fill" className="text-boba-danger" />}
              >
                {data.low_stock.length === 0 ? (
                  <div className="text-sm text-boba-inkSoft">All toppings in good supply 🎉</div>
                ) : (
                  <div className="space-y-2" data-testid="low-stock-list">
                    {data.low_stock.map((t) => (
                      <div key={t.id} className="flex items-center justify-between p-3 border border-red-200 bg-red-50 rounded-md">
                        <div className="font-semibold">{t.name}</div>
                        <div className="font-mono text-sm text-boba-danger">{t.stock} / {t.low_stock_threshold}</div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Slow Movers" action={<span className="text-xs text-boba-inkSoft">Lowest qty in range</span>}>
                {data.bottom_items.length === 0 ? (
                  <div className="text-sm text-boba-inkSoft py-3">Not enough data</div>
                ) : (
                  <div className="space-y-2">
                    {data.bottom_items.map((it, i) => (
                      <div key={i} className="flex items-center justify-between p-3 border border-boba-stoneLine rounded-md">
                        <div className="flex items-center gap-2">
                          <Ticket size={16} weight="duotone" className="text-boba-inkSoft" />
                          <span className="font-semibold">{it.name}</span>
                        </div>
                        <div className="font-mono text-sm text-boba-inkSoft">{it.qty} sold · {fmt(it.revenue)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
